/**
 * AbbreviationsPage — Task 25.7 (DEC-118). Consolidated abbreviation overview.
 *
 * Summary health cards (haulers / operators / HaulId-ready) + a defensive conflict
 * banner, then a Hauler Abbreviations table and an Operator Abbreviations table with
 * inline abbreviation editing (letters only, 2–4, uppercased) that reuses the existing
 * Task 25.2 / 25.3 PATCH endpoints. Missing abbreviations sort first.
 *
 * OPEN-203: the rule says why (no numbers: it goes in front of the ticket number) as soon as a
 * non-letter is typed, and a company whose name starts with digits gets a suggestion.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import {
  ABBREVIATION_RE,
  ABBREVIATION_RULE_TEXT,
  nameStartsWithDigit,
  suggestAbbreviation,
} from '@lodex-pro/shared/abbreviation';
import { Pill, fieldStyle, subText } from '../../ui/bits';
import {
  useAbbrevSummary,
  useAbbrevHaulers,
  useAbbrevOperators,
  useSetHaulerAbbreviation,
  useSetOperatorAbbreviation,
  type AbbrevHauler,
  type AbbrevOperator,
  abbrevSaveError,
  useTicketPrefixCollisions,
} from './hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

// ─── Inline editable abbreviation cell ───────────────────────────────────────

function EditableAbbrev({
  value,
  name,
  missingTone,
  pending,
  onSave,
}: {
  value: string | null;
  /** The company's name, for a suggestion when it starts with digits. */
  name: string;
  missingTone: 'danger' | 'warning';
  pending: boolean;
  onSave: (abbrev: string, onDone: () => void, onError: (err: unknown) => void) => void;
}): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {value !== null ? (
          <span style={{ fontWeight: 600 }}>{value}</span>
        ) : (
          <Pill label="NOT SET" tone={missingTone} />
        )}
        <Button
          type="button"
          variant="outline"
          onClick={(): void => {
            setInput(value ?? '');
            setError(null);
            setEditing(true);
          }}
        >
          Edit
        </Button>
      </div>
    );
  }

  // A name like "84 ENERGY" gives no letters to start from; offer some.
  const suggestion = nameStartsWithDigit(name) ? suggestAbbreviation(name) : null;

  const save = (): void => {
    if (!ABBREVIATION_RE.test(input.trim())) {
      setError(ABBREVIATION_RULE_TEXT);
      return;
    }
    setError(null);
    onSave(
      input.trim().toUpperCase(),
      () => {
        toast.success('Abbreviation saved');
        setEditing(false);
      },
      (err) => setError(abbrevSaveError(err)),
    );
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <input
        value={input}
        maxLength={4}
        autoFocus
        onChange={(e): void => {
          const next = e.target.value.toUpperCase();
          setInput(next);
          // Say why at once, not only on Save (WAV3 read as four letters).
          setError(/[^A-Z]/.test(next.trim()) ? ABBREVIATION_RULE_TEXT : null);
        }}
        style={{ ...fieldStyle, minHeight: '32px', maxWidth: '90px', textTransform: 'uppercase' }}
      />
      <Button
        type="button"
        disabled={pending}
        onClick={save}
        style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
      >
        Save
      </Button>
      <Button type="button" variant="outline" onClick={(): void => setEditing(false)}>
        Cancel
      </Button>
      {suggestion !== null && input !== suggestion && (
        <Button
          type="button"
          variant="ghost"
          onClick={(): void => {
            setInput(suggestion);
            setError(null);
          }}
        >
          Use {suggestion}
        </Button>
      )}
      {error !== null && (
        <span role="alert" style={{ color: 'var(--color-danger)', fontSize: '12px' }}>
          {error}
        </span>
      )}
    </div>
  );
}

function HaulerAbbrevCell({ hauler }: { hauler: AbbrevHauler }): React.JSX.Element {
  const set = useSetHaulerAbbreviation(hauler.id);
  return (
    <EditableAbbrev
      value={hauler.haulerAbbreviation}
      name={hauler.name}
      missingTone="danger"
      pending={set.isPending}
      onSave={(abbrev, onDone, onError): void =>
        set.mutate({ haulerAbbreviation: abbrev }, { onSuccess: onDone, onError })
      }
    />
  );
}

function OperatorAbbrevCell({ operator }: { operator: AbbrevOperator }): React.JSX.Element {
  const set = useSetOperatorAbbreviation(operator.id);
  return (
    <EditableAbbrev
      value={operator.abbreviation}
      name={operator.displayName ?? operator.name}
      missingTone="warning"
      pending={set.isPending}
      onSave={(abbrev, onDone, onError): void =>
        set.mutate({ abbreviation: abbrev }, { onSuccess: onDone, onError })
      }
    />
  );
}

// ─── Summary cards ───────────────────────────────────────────────────────────

function Card({
  title,
  value,
  ok,
}: {
  title: string;
  value: string;
  ok: boolean;
}): React.JSX.Element {
  return (
    <div
      style={{
        flex: 1,
        minWidth: '220px',
        backgroundColor: 'var(--color-bg-secondary)',
        border: `1px solid ${ok ? 'var(--color-border-primary)' : 'rgba(246, 182, 94,0.4)'}`,
        borderRadius: 'var(--radius-card)',
        padding: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <span style={{ ...subText, textTransform: 'none' }}>{title}</span>
        <Pill label={ok ? 'OK' : 'Attention'} tone={ok ? 'positive' : 'warning'} />
      </div>
      <div style={{ fontSize: '15px', fontWeight: 600 }}>{value}</div>
    </div>
  );
}

/**
 * 0205 — customers of one hauler that print the same ticket code today. New ones are refused;
 * these predate the guard. Fix one by recoding the hauler's private customer, or by changing the
 * Paelode abbreviation of the catalog operator.
 */
function TicketPrefixCollisions(): React.JSX.Element {
  const q = useTicketPrefixCollisions();
  const rows = q.data?.collisions ?? [];
  return (
    <section data-testid="ticket-prefix-collisions">
      <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '20px 0 12px' }}>
        Ticket code collisions
      </h3>
      {q.isLoading ? (
        <p style={subText}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ ...subText, textTransform: 'none' }}>
          No two customers of any hauler print the same ticket code.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hauler</TableHead>
              <TableHead>Ticket code</TableHead>
              <TableHead>Customers using it</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={`${c.tenantId}-${c.prefix}`}>
                <TableCell style={{ fontWeight: 500 }}>{c.tenantName}</TableCell>
                <TableCell style={{ fontWeight: 600 }}>{c.prefix}</TableCell>
                <TableCell>
                  {c.customers
                    .map(
                      (u) =>
                        `${u.name} (${u.isPlatformOperator ? 'catalog' : 'private'}${u.isActive ? '' : ', inactive'}, ${String(u.tickets)} tickets)`,
                    )
                    .join(' · ')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

export function AbbreviationsPage(): React.JSX.Element {
  const summary = useAbbrevSummary();
  const haulersQ = useAbbrevHaulers();
  const operatorsQ = useAbbrevOperators();

  const s = summary.data;
  const haulers = haulersQ.data?.haulers ?? [];
  const operators = operatorsQ.data?.operators ?? [];

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Abbreviations</h2>

      {/* Summary cards */}
      {s !== undefined && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Card
            title="Hauler abbreviations"
            value={`${formatNumber(s.haulers.hasAbbreviation)} of ${formatNumber(s.haulers.total)} haulers have abbreviations`}
            ok={s.haulers.missingAbbreviation === 0}
          />
          <Card
            title="Operator abbreviations"
            value={`${formatNumber(s.operators.hasAbbreviation)} of ${formatNumber(s.operators.totalActive)} operators have abbreviations`}
            ok={s.operators.missingAbbreviation === 0}
          />
          <Card
            title="HaulId ready"
            value={
              s.haulIdReady
                ? 'All haulers ready'
                : `${s.haulers.missingAbbreviation} hauler(s) missing — HaulIds cannot be generated for new jobs`
            }
            ok={s.haulIdReady}
          />
        </div>
      )}

      {/* Conflict banner (defensive — unique constraints should prevent these) */}
      {s !== undefined && s.conflicts.length > 0 && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-danger)',
            backgroundColor: 'rgba(243, 139, 139,0.1)',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: 'var(--color-danger)',
            fontSize: '14px',
          }}
        >
          {s.conflicts.length} abbreviation conflict(s) detected — HaulIds may collide.
          <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
            {s.conflicts.map((c) => (
              <li key={`${c.scope}-${c.abbreviation}`}>
                {c.scope} · {c.abbreviation} · {c.ids.length} records
              </li>
            ))}
          </ul>
        </div>
      )}

      <TicketPrefixCollisions />

      {/* Hauler abbreviations */}
      <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '20px 0 12px' }}>
        Hauler Abbreviations
      </h3>
      {haulersQ.isLoading ? (
        <p style={subText}>Loading…</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hauler Name</TableHead>
              <TableHead>Abbreviation</TableHead>
              <TableHead>Jobs (30d)</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {haulers.map((h) => (
              <TableRow key={h.id}>
                <TableCell style={{ fontWeight: 500 }}>{h.name}</TableCell>
                <TableCell>
                  <HaulerAbbrevCell hauler={h} />
                </TableCell>
                <TableCell>{formatNumber(h.jobCount)}</TableCell>
                <TableCell>
                  <Pill
                    label={h.isActive ? 'Active' : 'Suspended'}
                    tone={h.isActive ? 'positive' : 'muted'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Operator abbreviations */}
      <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '28px 0 12px' }}>
        Operator Abbreviations
      </h3>
      {operatorsQ.isLoading ? (
        <p style={subText}>Loading…</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Operator Name</TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead>Abbreviation</TableHead>
              <TableHead>Haulers</TableHead>
              <TableHead>Wells</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.map((o) => (
              <TableRow key={o.id}>
                <TableCell style={{ fontWeight: 500 }}>{o.name}</TableCell>
                <TableCell style={subText}>{o.displayName ?? '—'}</TableCell>
                <TableCell>
                  <OperatorAbbrevCell operator={o} />
                </TableCell>
                <TableCell>{formatNumber(o.haulerCount)}</TableCell>
                <TableCell>{formatNumber(o.wellCount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
